import { FC, useEffect, useMemo } from "react";
import { Box, Button, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchRecipes } from "../../store/recipeStore/recipeThunk";
import { ApiStatus, defaultRowsPerPageOptions } from "../../utils/api.types";

const RecipeList: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { recipes, status, rowCount } = useAppSelector((state) => state.recipes);

  useEffect(() => {
    dispatch(fetchRecipes());
  }, [dispatch]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "menuItemRef",
        headerName: "Menu Item Ref",
        flex: 1.4,
        minWidth: 180,
        renderCell: ({ row }: GridValueGetterParams) => (
          <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => navigate(`/inventory/recipes/${row._id}`)}>
            {row.menuItemRef}
          </span>
        ),
      },
      { field: "variantRef", headerName: "Variant", flex: 1, minWidth: 120, valueGetter: (p: GridValueGetterParams) => p.row.variantRef || "—" },
      { field: "components", headerName: "Components", flex: 0.8, minWidth: 110, valueGetter: (p: GridValueGetterParams) => (p.row.components || []).length },
      {
        field: "isActive",
        headerName: "Status",
        flex: 0.7,
        minWidth: 90,
        renderCell: ({ row }: GridValueGetterParams) => (
          <Chip size="small" label={row.isActive ? "Active" : "Inactive"} sx={{ color: row.isActive ? "#49a563" : "#c33b3b" }} />
        ),
      },
    ],
    [navigate]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", py: 1 }}>
        <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => navigate("/inventory/recipes/new")}>
          New Recipe
        </Button>
      </Box>
      {status !== ApiStatus.LOADING && (recipes?.length || 0) === 0 ? (
        <InventoryEmptyState
          icon={<MenuBookOutlinedIcon />}
          title="No Recipes Yet"
          description="Define your first recipe to map menu items to their ingredients so stock depletes automatically on every sale."
          actionLabel="Create Your First Recipe"
          onAction={() => navigate("/inventory/recipes/new")}
          caption="Need help? Check the Guide tab on inventory management"
        />
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={recipes}
            rowCount={rowCount}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}
    </Box>
  );
};

export default RecipeList;
